import { SEO } from "@/components/SEO";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Card, CardContent } from "@/components/ui/card";
import { AlertTriangle, Heart, MapPin, Wrench, CheckCircle, Clock, HelpCircle } from "lucide-react";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { SearchableFAQ } from "@/components/enhanced/SearchableFAQ";

const iconMap = {
  "heart": Heart,
  "map-pin": MapPin,
  "wrench": Wrench,
  "check-circle": CheckCircle,
  "clock": Clock,
  "help-circle": HelpCircle,
  "alert-triangle": AlertTriangle
};

type FAQ = {
  id: string;
  category: string;
  category_icon: string;
  question: string;
  answer: string;
  order_index: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
};

type GroupedFAQ = {
  category: string;
  icon: React.ComponentType<any>;
  questions: FAQ[];
};

const FAQ = () => {
  const [faqs, setFaqs] = useState<GroupedFAQ[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFAQs = async () => {
      try {
        const { data, error } = await supabase
          .from('faqs')
          .select('*')
          .eq('is_active', true)
          .order('category', { ascending: true })
          .order('order_index', { ascending: true });

        if (error) throw error;

        // Group FAQs by category
        const grouped = (data || []).reduce((acc, faq) => {
          const existingCategory = acc.find(g => g.category === faq.category);
          if (existingCategory) {
            existingCategory.questions.push(faq);
          } else {
            acc.push({
              category: faq.category,
              icon: iconMap[faq.category_icon as keyof typeof iconMap] || HelpCircle,
              questions: [faq]
            });
          }
          return acc;
        }, [] as GroupedFAQ[]);

        setFaqs(grouped);
      } catch (error) {
        console.error('Error fetching FAQs:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchFAQs();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4" />
          <p>Chargement des FAQ...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <SEO
        title="FAQ - Questions Fréquentes sur la Location de Châteaux Gonflables | Château de Clémentine"
        description="Toutes les réponses à vos questions sur la location de châteaux gonflables et sumos : prix, montage, transport, dimensions véhicule, sécurité, nettoyage, météo. Plus de 30 ans d'expérience pour vos fêtes inoubliables."
        keywords="FAQ château gonflable, questions location château, montage château gonflable, dimensions transport, prix location, sécurité enfants, Rhode-Saint-Genèse, Belgique, anniversaire, sumo gonflable"
        canonicalUrl="https://chateaudeclementine.com/faq"
      />
      <Header />
      
      <main>
        {/* Hero Section */}
        <section className="pt-24 pb-12 bg-gradient-sky">
          <div className="container mx-auto px-4">
            <div className="text-center max-w-4xl mx-auto">
              <Badge className="mb-4 gradient-primary text-white border-0">FAQ</Badge>
              <h1 className="text-4xl md:text-5xl font-display font-bold mb-6">
                <span className="bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
                  Questions Fréquentes
                </span>
              </h1>
              <p className="text-xl text-muted-foreground leading-relaxed">
                Plus de 30 ans d'expérience nous ont appris quelles sont vos préoccupations principales. 
                Trouvez ici toutes les réponses détaillées à vos questions sur la location de châteaux gonflables et sumos : 
                prix, transport, montage, sécurité, nettoyage et bien plus pour réussir vos fêtes inoubliables.
              </p>
            </div>
          </div>
        </section>

        {/* FAQ Content */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 max-w-6xl">
            <SearchableFAQ faqs={faqs} />
          </div>
        </section>

        {/* Contact CTA */}
        <section className="py-16 bg-gradient-sky">
          <div className="container mx-auto px-4 text-center">
            <Card className="max-w-2xl mx-auto shadow-card border-0">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-gradient-primary rounded-2xl flex items-center justify-center mx-auto mb-6 text-white">
                  <AlertTriangle className="w-8 h-8" />
                </div>
                <h3 className="text-2xl font-display font-bold mb-4">
                  Une autre question ?
                </h3>
                <p className="text-muted-foreground mb-6 leading-relaxed">
                  Vous ne trouvez pas la réponse à votre question ? 
                  Notre équipe expérimentée est là pour vous aider !
                </p>
                <a 
                  href="/#contact"
                  className="inline-flex items-center justify-center px-6 py-3 bg-gradient-primary text-white font-semibold rounded-xl hover:shadow-lg transition-all duration-300"
                >
                  Nous Contacter
                </a>
              </CardContent>
            </Card>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
};

export default FAQ;